import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const StatisticsPanel = ({ shipments }) => {
  const statistics = useMemo(() => {
    const now = new Date();
    const currentMonth = now?.getMonth();
    const currentYear = now?.getFullYear();

    // Monthly shipments
    const monthlyShipments = shipments?.filter(shipment => {
      const shipDate = new Date(shipment?.shippingDate);
      return shipDate?.getMonth() === currentMonth && shipDate?.getFullYear() === currentYear;
    });

    // Total cost this month
    const monthlyCost = monthlyShipments?.reduce((sum, shipment) => sum + (shipment?.cost || 0), 0);

    // Favorite destinations (top 3)
    const destinationCounts = {};
    shipments?.forEach(shipment => {
      const city = shipment?.destination?.split(',')?.[1]?.trim() || 'Unknown';
      destinationCounts[city] = (destinationCounts?.[city] || 0) + 1;
    });
    const topDestinations = Object.entries(destinationCounts)?.sort((a, b) => b?.[1] - a?.[1])?.slice(0, 3)?.map(([city, count]) => ({ city, count }));

    // Delivery success rate
    const deliveredCount = shipments?.filter(s => s?.status?.toLowerCase() === 'delivered')?.length || 0;
    const successRate = shipments?.length > 0 ? ((deliveredCount / shipments?.length) * 100)?.toFixed(0) : 0;

    return {
      monthlyShipments: monthlyShipments?.length || 0,
      monthlyCost,
      topDestinations,
      totalShipments: shipments?.length || 0,
      successRate
    };
  }, [shipments]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      {/* Monthly Shipments */}
      <div className="bg-card rounded-lg border border-border shadow-warm p-4">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="Package" size={20} className="text-primary" />
          </div>
          <div>
            <p className="text-xs font-body text-muted-foreground">This Month</p>
            <p className="text-2xl font-heading font-bold text-foreground">{statistics?.monthlyShipments}</p>
          </div>
        </div>
        <p className="text-xs font-body text-muted-foreground">Shipments sent</p>
      </div>

      {/* Monthly Cost */}
      <div className="bg-card rounded-lg border border-border shadow-warm p-4">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="DollarSign" size={20} className="text-success" />
          </div>
          <div>
            <p className="text-xs font-body text-muted-foreground">Monthly Cost</p>
            <p className="text-2xl font-heading font-bold text-foreground">${statistics?.monthlyCost?.toFixed(2)}</p>
          </div>
        </div>
        <p className="text-xs font-body text-muted-foreground">Total shipping cost</p>
      </div>

      {/* Total Shipments */}
      <div className="bg-card rounded-lg border border-border shadow-warm p-4">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
            <Icon name="TrendingUp" size={20} className="text-warning" />
          </div>
          <div>
            <p className="text-xs font-body text-muted-foreground">All Time</p>
            <p className="text-2xl font-heading font-bold text-foreground">{statistics?.totalShipments}</p>
          </div>
        </div>
        <p className="text-xs font-body text-muted-foreground">Total shipments</p>
      </div>

      {/* Success Rate */}
      <div className="bg-card rounded-lg border border-border shadow-warm p-4">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" size={20} className="text-success" />
          </div>
          <div>
            <p className="text-xs font-body text-muted-foreground">Success Rate</p>
            <p className="text-2xl font-heading font-bold text-foreground">{statistics?.successRate}%</p>
          </div>
        </div>
        <p className="text-xs font-body text-muted-foreground">Delivered on time</p>
      </div>
    </div>
  );
};

export default StatisticsPanel;